"use client";
import useRegionAndLocale from "@/hooks/useRegionAndLocale";
import Link from "@/components/UI/Link/Link";
import { usePathname } from "next/navigation";
import { FC, HTMLAttributeAnchorTarget } from "react";
import SelectNavigation from "../SelectNavigation/SelectNavigation";
import styles from "./SideNavigation.module.scss";
import { StoreRegion } from "@/lib/_config/stores";

interface Props {
	title: string;
	links: {
		text: string;
		href: string;
		target?: HTMLAttributeAnchorTarget;
	}[];
}

const SideNavigation: FC<Props> = ({ title, links }) => {
	const { region, lang } = useRegionAndLocale();
	const path = usePathname();

	const linkIsActive = (link: string) => {
		if (link === path) {
			return true;
		}

		const pathStripped = path?.replace(`/${region}/${lang}/`, "/");

		if (link === "account/" || link === "account") {
			return pathStripped === "/account";
		}

		return pathStripped.indexOf(link) > -1;
	};

	// Check the URL for certain pages and do not show the navigation
	if (checkUrlContainsPattern(path)) return <></>;

	return (
		<>
			<SelectNavigation
				title={title}
				links={links}
				className={styles.mobileNav}
			/>
			<aside className={styles.aside}>
				<nav className={styles.nav}>
					{links.map((link, index) => {
						if (!link) {
							return null;
						}
						const sanitizedHref = cleanHref(link.href, region, lang);

						return (
							<Link
								key={`${link.toString()}-${index}`}
								className={linkIsActive(link.href) ? styles.active : ""}
								href={
									sanitizedHref.includes("customer-care")
										? `/${region}/${lang}/${sanitizedHref.replace(
												`${region}/${lang}/`,
												"",
										  )}` // Ensure no duplication
										: `/${sanitizedHref}`
								}
								target={link.target}
							>
								{link.text}
							</Link>
						);
					})}
				</nav>
			</aside>
		</>
	);
};

export default SideNavigation;

function checkUrlContainsPattern(url: string) {
	const pattern = /\/(invoices|orders|lists)\/\d+/;
	return pattern.test(url);
}

const cleanHref = (href: string, region: StoreRegion, lang: string) => {
	if (!href) {
		return "";
	}

	let link = typeof href === "string" ? href : "";
	// Handle the global store specifically
	if (region === "global" && typeof link === "string") {
		link = link.replace(/global\/+/, ""); // Remove 'global' and extra slashes
	}
	const prefix = `${region}/${lang}`;
	// Check if prefix is already present
	const hasPrefix =
		href.includes(`/${prefix}/`) || href.startsWith(`/${prefix}/`);

	if (
		(href.includes("account") && !hasPrefix) ||
		(href.startsWith("logout") && !href.includes(prefix))
	) {
		link = `${prefix}/${href}`;
	}
	// Remove store/lang prefix and clean redundant slashes
	return link
		.replace(/\/{2,}/g, "/") // Replace multiple slashes with one
		.replace(/^\/+/, ""); // Remove leading slashes
};
